import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  ACCEPT_LANGUAGE_MAP,
  AppLang,
  DEFAULT_LANGUAGE,
  HTML_LANG_MAP,
  STORAGE_KEY,
  TEXT_DIRECTION_MAP,
} from './language.tokens';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly document = inject(DOCUMENT);
  private readonly translate = inject(TranslateService);

  private readonly _current = signal<AppLang>(DEFAULT_LANGUAGE);

  readonly current = this._current.asReadonly();

  init(): AppLang {
    const stored = this.readStoredLanguage();
    this.applyLanguage(stored, false);
    return stored;
  }

  switch(lang: AppLang): void {
    if (this._current() === lang) {
      return;
    }

    this.applyLanguage(lang, true);
  }

  getAcceptLanguageHeader(): string {
    return ACCEPT_LANGUAGE_MAP[this._current()];
  }

  async initWithTranslations(): Promise<AppLang> {
    const lang = this.init();
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    this.translate.addLangs(['fa', 'en']);
    await firstValueFrom(this.translate.use(lang));
    return lang;
  }

  async switchWithTranslations(lang: AppLang): Promise<void> {
    this.applyLanguage(lang, true);
    await firstValueFrom(this.translate.use(lang));
  }

  private applyLanguage(lang: AppLang, persist: boolean): void {
    this._current.set(lang);

    if (persist) {
      localStorage.setItem(STORAGE_KEY, lang);
    }

    this.updateDocumentAttributes(lang);
  }

  private readStoredLanguage(): AppLang {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'fa' || stored === 'en' ? stored : DEFAULT_LANGUAGE;
  }

  private updateDocumentAttributes(lang: AppLang): void {
    const html = this.document.documentElement;
    const body = this.document.body;
    const direction = TEXT_DIRECTION_MAP[lang];

    html.lang = HTML_LANG_MAP[lang];
    html.dir = direction;

    body.classList.remove('is-rtl', 'is-ltr');
    body.classList.add(direction === 'rtl' ? 'is-rtl' : 'is-ltr');
  }
}
